<template>
  <ValidationObserver ref="companiesStorePostsValidation" tag="div" class="c-grid">
    <div class="c-grid__items">
      <section class="c-grid__item c-grid__item__s__12 c-grid__item__m__7 c-grid__item__l__7">
        <!-- 対象サービス -->
        <KkBox v-if="isMultiPost" border class="selectServices">
          <template #content>
            <h2 class="name">{{ $t('pages.CompaniesStorePost.targetService.title') }}</h2>
            <div>
              <KkInfo icon="hint">
                {{ $t('pages.CompaniesStorePost.targetService.info') }}
              </KkInfo>
            </div>
            <KkFormLabel class="selectServices__checkboxTitle" :text="`${$t('pages.CompaniesStorePost.targetService.selectService')}`" />
            <div class="selectServices__checkbox">
              <KkCheckbox
                v-for="service in availableServices"
                :id="`${service}_id`"
                :key="service"
                :label="$t(`pages.CompaniesStorePost.targetService.services.${convertToCamelCase(service)}`)"
                :model-value="post.services.includes(service)"
                @update:modelValue="handleChangeService(service)"
              />
            </div>
            <ValidationError id="services" :error="selectServicesValidation" />
          </template>
        </KkBox>

        <!-- 投稿を作成 -->
        <KkBox v-if="showCreatePost" border>
          <template #content>
            <h2 class="name">{{ createPostTitle }}</h2>
            <KkInfo v-if="postInfo.length > 0" icon="hint">
              {{ postInfo }}
            </KkInfo>

            <!-- 対象店舗 -->
            <div class="selectStore">
              <SelectStore :selected-store-count="post.storeUuids.length" @open-store-modal="$emit('open-store-modal')">
                <div class="note">
                  <KkInfo icon="hint">
                    {{ $t('pages.CompaniesStorePost.noticeManyStoresPost') }}
                  </KkInfo>
                </div>
                <ValidationError id="stores" :error="selectStoresValidation" />
              </SelectStore>
            </div>

            <!-- 投稿カテゴリ -->
            <div v-if="categories.length !== 0" class="section">
              <KkBox border>
                <template #content>
                  <KkFormLabel :text="`${ $t('pages.CompaniesStoreSnsPost.index.editModal.category') }${ $t('labels.optionalLabel') }`" />
                  <Select
                    :options="categoryOptions"
                    :model-value="categoryId"
                    @update:model-value="updateCategory"
                  />
                  <div class="note">
                    <KkInfo icon="hint">
                      {{ $t('pages.CompaniesStorePost.categoryInfo') }}
                    </KkInfo>
                  </div>
                </template>
              </KkBox>
            </div>

            <!-- 投稿タイプ -->
            <div class="section">
              <KkBox border>
                <template #content>
                  <KkFormLabel :text="`${ $t('pages.CompaniesStorePost.selectPostType') }${ $t('labels.requiredLabel') }`" />
                  <KkSelect
                    :options="topicTypeOptions"
                    :model-value="selectedTopicType"
                    @update:model-value="clickMenu($event)"
                  />
                  <ValidationError id="topicType" :error="topicTypeValidation" />
                </template>
              </KkBox>
            </div>

            <template v-if="post.topicType">
              <!-- 写真 -->
              <div class="section">
                <PostImage ref="postImageComponent" />
              </div>

              <ValidationError id="mediaOrSummary" :error="mediaOrSummaryValidation" />

              <!-- タイトル -->
              <KkPostTitle />

              <!-- 開始日・終了日・時間 -->
              <Event v-show="showEventType" />

              <ValidationError id="stores" :error="eventDateTimeValidation" />

              <!-- 詳細 -->
              <div class="section">
                <Summary
                  :available-parameters="parameters"
                  :parameter-loading="parameterLoading"
                />
              </div>

              <ValidationError id="mediaOrSummary" :error="mediaOrSummaryValidation" />

              <!-- ボタンの追加・ボタンのリンク -->
              <div v-if="showCallToActionType" class="section">
                <CallToAction
                  :available-parameters="parameters"
                  :parameter-loading="parameterLoading"
                />
              </div>

              <!-- クーポンコード -->
              <!-- Offer配下のValidationProviderが、特典を利用していない際に発火しないようにv-ifを使用 -->
              <div v-if="showOfferType" class="section">
                <Offer
                  :available-parameters="parameters"
                  :parameter-loading="parameterLoading"
                />
              </div>
            </template>
          </template>
        </KkBox>
      </section>

      <section class="c-grid__item c-grid__item__s__12 c-grid__item__m__5 c-grid__item__l__5">
        <KkPromotionCompaniesStorePostsSetting
          :need-approval="needApproval"
          :is-post-edit="isPostEdit"
          @open-action-modal="openActionModal(true)"
          @cancel-alert="cancelAlert"
        />

        <div v-if="needApproval" class="approvalHistory">
          <KkBox tag="div" border>
            <template #content>
              <h3 class="name">{{ $t('pages.CompaniesStorePost.approvalHistory') }}</h3>
              <div id="scroll" class="approvalHistoryContainer">
                <div v-if="post.notes.length === 0">
                  <h4>{{ $t('pages.CompaniesStorePost.emptyApprovalHistory') }}</h4>
                </div>
                <div v-for="(note, i) in post.notes" :key="i" class="note">
                  <h4>
                    {{ noteHeader(note) }}
                  </h4>
                  <div class="comment" :class="noteBodyClassName(note)">{{ noteBodyComment(note) }}</div>
                </div>
              </div>
            </template>
          </KkBox>
        </div>

        <!-- プレビュー -->
        <div v-if="isDisplayPreview" class="previews">
          <KkInfo icon="hint" class="previews__info">
            {{ $t('pages.CompaniesStorePost.previewInfo') }}
          </KkInfo>
          <template v-for="service in post.services">
            <div v-if="service === 'docomo'" :key="service" class="previews__item">
              <KkDocomoPostPreview />
            </div>
            <div v-if="service === 'google_map'" :key="service" class="previews__item">
              <KkGoogleMapPostPreview />
            </div>
            <div v-if="service === 'yahoo_map'" :key="service" class="previews__item">
              <KkYahooMapPostPreview />
            </div>
          </template>
        </div>
      </section>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import ImgIconPlusRed from '../../../../images/common/icon_plus_red.svg'
import ImgIconCheck from '../../../../images/common/icon_check.svg'
import googleMapIcon from '../../../../images/common/site_google.png'
import docomoIcon from '../../../../images/common/docomo.png'
import { camelCase } from '../../modules/string_converter'
import SelectStore from '../SelectStore'
import PostImage from './PostImage'
import Event from './Event'
import Summary from './Summary'
import Offer from './Offer'
import CallToAction from './CallToAction'
import Select from '../Select'
import ValidationError from '../ValidationError'
import { KkPromotionCompaniesStorePostsSetting } from '../domains/promotion/CompaniesStorePosts'
import { KkBox, KkFormLabel, KkSelect, KkCheckbox } from '../base'
import { KkInfo } from '../ui'
import moment from 'moment'
import { actionEvent } from 'src/biz_spa/modules/googleAnalytics'
import { KkPostTitle, KkGoogleMapPostPreview, KkDocomoPostPreview, KkYahooMapPostPreview } from '../../domains/post'

export default {
  name: 'CompaniesStorePostsBase',
  components: {
    SelectStore,
    PostImage,
    Event,
    Summary,
    Offer,
    CallToAction,
    ValidationError,
    KkPromotionCompaniesStorePostsSetting,
    KkGoogleMapPostPreview,
    KkDocomoPostPreview,
    KkYahooMapPostPreview,
    KkBox,
    KkFormLabel,
    KkSelect,
    KkInfo,
    Select,
    KkPostTitle,
    KkCheckbox
  },
  props: {
    needApproval: {
      type: Boolean,
      default: false
    },
    isPostEdit: {
      type: Boolean,
      required: true
    },
    availableServices: {
      type: Array,
      default: () => []
    }
  },
  emits: ['drafted-action', 'open-action-modal', 'open-store-modal', 'change-services'],
  data() {
    return {
      categoryId: '',
      googleMapIcon,
      docomoIcon,
    }
  },
  computed: {
    ...mapState({
      post: state => state.companiesStorePosts.post,
      reservationDateTime: state => state.companiesStorePosts.reservationDateTime,
      eventStartedTime: state => state.companiesStorePosts.eventStartedTime,
      eventEndedTime: state => state.companiesStorePosts.eventEndedTime,
      categories: state => state.companiesSnsCategories.data,
    }),
    ...mapGetters('companiesStorePosts', [
      'topicTypes',
      'selectTopicType',
      'showEventType',
      'showCallToActionType',
      'showOfferType',
      'showSubmitActionModalType',
      'selectedTopicType',
    ]),
    ...mapGetters({
      availableParameters: 'companiesParameters/availableParameters',
      parameterLoading: 'companiesParameters/loading',
    }),
    topicTypeOptions() {
      const options = {}
      Object.keys(this.topicTypes).forEach(key => {
        options[this.topicTypes[key]] = key
      })
      return options
    },
    approvalRequestNotes() {
      if(!this.post && !this.post.notes) {
        return []
      }

      return this.post.notes
    },
    isMultiPost() {
      return this.availableServices.length >= 2
    },
    createPostTitle() {
      if (this.isMultiPost) return this.$t('pages.CompaniesStorePost.createPostTitle')

      if (this.availableServices.includes('google_map')) {
        return this.$t('pages.CompaniesStorePost.tabs.googleMap')
      } else if (this.availableServices.includes('docomo')) {
        return this.$t('pages.CompaniesStorePost.tabs.docomo')
      } else if (this.availableServices.includes('yahoo_map')) {
        return this.$t('pages.CompaniesStorePost.tabs.yahooMap')
      }
      return ''
    },
    imgIconCheck() {
      return ImgIconCheck
    },
    imgIconPlusRed() {
      return ImgIconPlusRed
    },
    postStatus() {
      if (this.post.storeUuids.length === 0) return this.$t('pages.CompaniesStorePost.storeNotSelected')
      return this.$t('pages.CompaniesStorePost.storeSelectedCaption', {count: this.post.storeUuids.length, type: this.selectTopicType})
    },
    selectServicesValidation() {
      return this.post.services.length ? '' : this.$t('pages.CompaniesStorePost.targetService.required')
    },
    selectStoresValidation() {
      return this.post.storeUuids.length ? '' : '店舗は必須項目です'
    },
    mediaOrSummaryValidation() {
      // googleの時だけ必要なバリデーション
      if (!this.isOnlyGooglePost) return ''
      return (
        this.post.topicType === 'STANDARD' && !this.post.postImages.length && !this.post.summary
      ) ? this.$t('pages.CompaniesStorePost.noticeInputEitherTextOrImage') : ''
    },
    topicTypeValidation() {
      return this.post.topicType ? '' : '投稿タイプは必須項目です'
    },
    categoryOptions() {
      const results = {}
      results[this.$t('labels.notSet')] = ''
      this.categories.forEach(category => results[category.name] = category.id)
      return results
    },
    eventDateTimeValidation() {
      // イベント系のタイプでない場合はエラーにはならない
      if (!this.showEventType) return ''

      const newDate = new Date()
      const currentDateTime = new Date(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate(),
        newDate.getHours(),
        newDate.getMinutes()
      )
      const startedTime = this.eventStartedTime ? this.eventStartedTime.split(':') : '00:00'.split(':')
      const startedDateTime = new Date(
        this.post.event.startedAt.getFullYear(),
        this.post.event.startedAt.getMonth(),
        this.post.event.startedAt.getDate(),
        parseInt(startedTime[0], 10),
        parseInt(startedTime[1], 10)
      )
      const endedTime = this.eventEndedTime ? this.eventEndedTime.split(':'): '00:00'.split(':')
      const endedDateTime = new Date(
        this.post.event.endedAt.getFullYear(),
        this.post.event.endedAt.getMonth(),
        this.post.event.endedAt.getDate(),
        parseInt(endedTime[0], 10),
        parseInt(endedTime[1], 10)
      )

      // 現在の日時よりも指定された endedDateTime のほうが過去の場合はエラー
      if (endedDateTime.getTime() <= currentDateTime.getTime()) return this.$t('pages.CompaniesStorePost.FutureEventEndedAtDateTime')
      // endedDateTime よりも startedDateTime のほうが過去の場合はエラー
      if (endedDateTime.getTime() <= startedDateTime.getTime()) return this.$t('pages.CompaniesStorePost.EventStartedAtBeforeEndedAt')
      // endedDateTime よりも 投稿予約日時が未来の場合はエラー
      if (this.reservationDateTime && endedDateTime.getTime() <= new Date(this.reservationDateTime).getTime()) return this.$t('pages.CompaniesStorePost.EventEndedAtLaterThanReservationDatetime')
      return ''
    },
    isDisplayPreview() { return !!this.selectedTopicType },
    parameters() { return this.availableParameters.filter(v => v != 'REVIEWER_NAME') },
    isOnlyGooglePost() {
      return this.post.services.length === 1 && this.post.services.includes('google_map')
    },
    includesDocomo() {
      if (!this.post.services) return false
      return this.post.services.includes('docomo')
    },
    postInfo() {
      let info = ''
      if (this.post.services.includes('google_map')) {
        info += this.$t('pages.CompaniesStorePostsNew.info')
      }
      if (this.post.services.includes('docomo')) {
        info += this.$t('pages.CompaniesStorePostsNew.docomoInfo')
      }
      return info
    },
    showCreatePost() {
      if (!this.isMultiPost) return true // 1つのサービスのみの場合は常時表示
      return this.post.services.length > 0
    },
  },
  created() {
    this.categoryId = this.post.category?.id
    this.readParameters()
  },
  methods: {
    ...mapMutations('companiesStorePosts', [
      'updateCategory',
      'updateTopicType',
      'updateSelectedTopicType',
    ]),
    ...mapActions({
      readParameters: 'companiesParameters/readParameters',
    }),
    async validate() {
      const postImageValidate = await this.validatePostImage()
      if (!postImageValidate) return false

      const result = await this.$refs.companiesStorePostsValidation.validate()
      if (!result.valid) {
        return false
      }
      let isValid = this.selectStoresValidation === '' && this.mediaOrSummaryValidation === '' && this.topicTypeValidation === ''
      if(this.showSubmitActionModalType !== 'draft') {
        isValid = isValid && this.eventDateTimeValidation === ''
      }
      return isValid
    },
    async validatePostImage() {
      if (this.$refs.postImageComponent && this.$refs.postImageComponent.$refs.postImageObserver) {
        const { results } = await this.$refs.postImageComponent.$refs.postImageObserver.validate()
        const postImageErrors = results[this.$t('components.PostImage.image')].errors
        if (postImageErrors) {
          // フォームで捕捉したいエラーは「画像を選択してください」のエラーのみ
          const hasError = postImageErrors.some(error => error.includes(this.$t('validations.requiredImage')))
          return !hasError
        }
      }
    },
    async openActionModal(reservationDateTime) {
      const validate = await this.validate()
      if (!validate) {
        return alert(this.$t('messages.invalid'))
      }
      this.$emit('open-action-modal', reservationDateTime)
    },
    async createDraftedPost() {
      const validate = await this.validate()
      if (!validate) {
        return alert(this.$t('messages.invalid'))
      }
      this.$emit('drafted-action')
    },
    cancelAlert() {
      if (confirm(this.$t('labels.cancelInput'))) {
        this.$router.push({ path: '/s/companies/' + this.$route.params.company_uuid + '/posts' })
      }
    },
    formatDate(dateStr){
      return moment(dateStr).format(this.$t('format.datetime.default'))
    },
    noteHeader(note) {
      let category = 'applicantFormat'

      if(note.commenterName === '') {
        category = 'batchRejectFormat'
      } else if (note.kind === 'approver_comment') {
        category = 'approverFormat'
      }

      return this.$t(`pages.CompaniesStorePost.comment.${category}`, { commentedAt: this.formatDate(note.commentedAt), commenterName: note.commenterName })
    },
    // NOTE: bodyがない場合定型文を出す仕様だが、承認者でコメントがない場合一律差し戻しというのは
    // 別の別の申請パターンやステータス変更に対応した時に危うい。その際はバックエンドも含めて検討する必要があり。
    // 今のところ申請者、承認者とも一択なのでこの対応。
    noteBodyComment(note) {
      if (note.body.length === 0) {
        if (note.kind === 'applicant_comment') {
          return this.$t('pages.CompaniesStorePost.comment.requestedApproval')
        } else {
          return this.$t('pages.CompaniesStorePost.comment.revertedApproval')
        }
      }
      return note.body
    },
    noteBodyClassName(note) {
      if(note.commenterName === '' || note.body.length === 0) {
        return 'comment--system'
      } else if (note.kind === 'approver_comment') {
        return 'comment--approver'
      } else if (note.kind === 'applicant_comment') {
        return 'comment--applicant'
      }
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('#scroll')
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    clickMenu(key) {
      this.updateTopicType(key)
      this.updateSelectedTopicType(key)
      if (!this.isPostEditMode) {
        actionEvent('SelectPostType')
      }
    },
    convertToCamelCase(value) {
      return camelCase(value)
    },
    handleChangeService(service) {
      const newArray = [...this.post.services]
      newArray.includes(service)
        ? newArray.splice(newArray.indexOf(service), 1)
        : newArray.push(service)
      this.$emit('change-services', newArray)
    }
  }
}
</script>

<style lang="scss" scoped>
.c-grid {
  margin-top: 16px;
}
.selectServices {
  margin-bottom: 32px;
  &__checkboxTitle {
    margin-top: 16px;
  }
  &__checkbox {
    margin-top: 16px;
    display: flex;
    gap: 24px;
  }
}
.name {
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  line-height: 1.33;
}
.selectStore {
  margin-top: 32px;
  border: 1px solid #E4E7EB;
}
.note {
  margin-top: 16px;
}

.previews {
  margin-top: 24px;

  &__info {
    margin-right: 8px;
  }

  &__item {
    margin-top: 24px;
  }
}
.section {
  margin-top: 32px;
}
.approvalHistory {
  height: 500px;
  margin: 16px 0;

  :deep(.box) {
    height: calc(100% - 32px);
  }

  :deep(.box__content) {
    height: 100%;
  }

  .approvalHistoryContainer {
    background-color: #FFFFFF;
    border: 1px solid #E3E3E3;
    box-sizing: border-box;
    border-radius: 4px;
    height: 85%;
    overflow-y: scroll;
    box-shadow: 0 0 0 rgba(63, 63, 68, .05);

    .note {
      margin: 16px;
      padding: 0 0 10px;

      h4 {
        margin: 0;
      }

      .comment {
        margin-top: 6px;
        padding: 8px 16px;
        border-radius: 16px;
        font-size: 14px;
        line-height: 24px;
        width: fit-content;
        word-break: break-all;

        &--applicant {
          background-color: #ECEDF0;
        }

        &--approver {
          background-color: #46555F;
          color: #F8F9FA;
        }

        &--system {
          background-color: #FFFFFF;
          padding: 0;
        }
      }
    }
  }
}
</style>
